import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ContractStatusBanner } from '../../components/banking/ContractStatusBanner';
import { KycStatusCard } from '../../components/banking/KycStatusCard';
import {
  FileCheck,
  Users,
  TrendingUp,
  ArrowUpRight,
  Clock,
  AlertTriangle,
  Eye,
  EyeOff,
  Plus,
  DollarSign,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export function SupplierDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);
  const [revenuePeriod, setRevenuePeriod] = useState<'3' | '6' | '12'>('6');

  // Fetch balance
  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['supplier', 'balance'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/balance');
      return response.data;
    },
  });

  // Fetch charge totals
  const { data: chargeTotals, isLoading: totalsLoading } = useQuery({
    queryKey: ['supplier', 'charge-totals'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/charges/totals');
      return response.data;
    },
  });

  // Fetch recent charges
  const { data: recentCharges, isLoading: chargesLoading } = useQuery({
    queryKey: ['supplier', 'charges', 'recent'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/charges', { params: { limit: 5 } });
      return response.data;
    },
  });

  // Fetch revenue metrics
  const { data: revenueData } = useQuery({
    queryKey: ['supplier', 'revenue'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/revenue/summary');
      return response.data;
    },
  });

  // Fetch monthly revenue for chart
  const { data: monthlyRevenue, isLoading: revenueLoading } = useQuery({
    queryKey: ['supplier', 'revenue', 'monthly', revenuePeriod],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/supplier/revenue/monthly', {
          params: { months: revenuePeriod },
        });
        return response.data;
      } catch {
        return null;
      }
    },
  });

  // Fetch contract status
  const { data: contractData } = useQuery({
    queryKey: ['banking', 'contract-status'],
    queryFn: async () => {
      try {
        const response = await api.get('/banking/contract-status');
        return response.data;
      } catch {
        return {
          contractStatus: 'ACTIVE',
          kycOriginStatus: 'APPROVED',
          kycDestinationStatus: 'APPROVED',
        };
      }
    },
  });

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseInt(value, 10) || 0 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-blue-500/20 text-blue-400';
      case 'PAID': return 'bg-green-500/20 text-green-400';
      case 'EXPIRED': return 'bg-red-500/20 text-red-400';
      case 'CANCELLED': return 'bg-surface-500/20 text-surface-400';
      default: return 'bg-surface-500/20 text-surface-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Ativa';
      case 'PAID': return 'Paga';
      case 'EXPIRED': return 'Vencida';
      case 'CANCELLED': return 'Cancelada';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Contract Status Banner */}
      {contractData && (
        <ContractStatusBanner
          contractStatus={contractData.contractStatus}
          kycOriginStatus={contractData.kycOriginStatus}
          kycDestinationStatus={contractData.kycDestinationStatus}
        />
      )}

      {/* KYC Status Card */}
      <KycStatusCard />

      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Olá, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-surface-400">Painel do Fornecedor</p>
        </div>
        <Button onClick={() => navigate('/banking/supplier/charges')}>
          <Plus size={20} className="mr-2" />
          Nova Cobrança
        </Button>
      </div>

      {/* Balance Card */}
      <Card className="relative overflow-hidden border-accent-500/30">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent-600/20 via-accent-700/10 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        
        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-accent-500/20">
                <DollarSign size={20} className="text-accent-400" />
              </div>
              <span className="text-surface-300 font-medium">Saldo disponível para saque</span>
            </div>
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 text-surface-400 hover:text-white rounded-lg hover:bg-surface-700/50 transition-colors"
            >
              {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          
          <div className="mb-6">
            {balanceLoading ? (
              <div className="h-10 w-48 bg-surface-700 rounded animate-pulse" />
            ) : (
              <span className="text-4xl font-bold text-white">
                {showBalance ? formatCurrency(balanceData?.availableBalance || 0) : '••••••'}
              </span>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/banking/supplier/charges')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-accent-500/50 transition-all"
            >
              <FileCheck size={24} className="text-accent-400" />
              <span className="text-xs text-surface-300">Cobrar</span>
            </button>
            <button
              onClick={() => navigate('/banking/supplier/withdrawals')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-accent-500/50 transition-all"
            >
              <ArrowUpRight size={24} className="text-accent-400" />
              <span className="text-xs text-surface-300">Sacar</span>
            </button>
            <button
              onClick={() => navigate('/banking/supplier/customers')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-accent-500/50 transition-all"
            >
              <Users size={24} className="text-accent-400" />
              <span className="text-xs text-surface-300">Clientes</span>
            </button>
            <button
              onClick={() => navigate('/banking/supplier/reports')}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-surface-800/80 hover:bg-surface-700 border border-surface-700 hover:border-accent-500/50 transition-all"
            >
              <TrendingUp size={24} className="text-accent-400" />
              <span className="text-xs text-surface-300">Relatórios</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-surface-400">Pendentes</p>
              <p className="text-lg font-semibold text-white">
                {totalsLoading ? '-' : chargeTotals?.active?.count || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-xs text-surface-400">Recebido (mês)</p>
              <p className="text-lg font-semibold text-white">
                {totalsLoading ? '-' : formatCurrency(chargeTotals?.paid?.total || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-400" />
            </div>
            <div>
              <p className="text-xs text-surface-400">Vencidas</p>
              <p className="text-lg font-semibold text-white">
                {totalsLoading ? '-' : chargeTotals?.expired?.count || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
              <Users size={20} className="text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-surface-400">Clientes</p>
              <p className="text-lg font-semibold text-white">
                {revenueData?.customerCount || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Charges & Overdue Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Charges */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Cobranças Recentes</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/banking/supplier/charges')}
              >
                Ver todas
              </Button>
            </div>

            {chargesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : recentCharges?.charges?.length > 0 ? (
              <div className="space-y-2">
                {recentCharges.charges.slice(0, 5).map((charge: any) => (
                  <div
                    key={charge.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                    onClick={() => navigate(`/banking/supplier/charges/${charge.id}`)}
                  >
                    <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                      <FileCheck size={20} className="text-surface-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {charge.description || `Cobrança #${charge.correlationId?.slice(-8)}`}
                      </p>
                      <p className="text-xs text-surface-400">
                        {formatDate(charge.createdAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-white">
                        {formatCurrency(charge.amount)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(charge.status)}`}>
                        {getStatusLabel(charge.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileCheck size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma cobrança recente</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate('/banking/supplier/charges')}
                >
                  Criar primeira cobrança
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Overdue Alerts */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-400" />
                Cobranças Vencidas
              </h2>
            </div>

            {chargeTotals?.expired?.count > 0 ? (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-red-400">Total vencido</span>
                    <span className="text-lg font-bold text-red-400">
                      {formatCurrency(chargeTotals.expired.total)}
                    </span>
                  </div>
                  <p className="text-xs text-surface-400">
                    {chargeTotals.expired.count} cobrança(s) vencida(s)
                  </p>
                </div>

                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => navigate('/banking/supplier/charges?status=EXPIRED')}
                >
                  Ver cobranças vencidas
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-3">
                  <DollarSign size={32} className="text-green-400" />
                </div>
                <p className="text-green-400 font-medium">Tudo em dia!</p>
                <p className="text-sm text-surface-400 mt-1">
                  Nenhuma cobrança vencida
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Receita Mensal</h2>
            <div className="flex items-center gap-2">
              {(['3', '6', '12'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setRevenuePeriod(period)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    revenuePeriod === period
                      ? 'bg-accent-500 text-white'
                      : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                  }`}
                >
                  {period} meses
                </button>
              ))}
            </div>
          </div>

          {revenueLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : monthlyRevenue?.data?.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyRevenue.data}>
                  <XAxis 
                    dataKey="month" 
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `R$${(value / 100).toLocaleString('pt-BR', { notation: 'compact' })}`}
                  />
                  <Tooltip
                    formatter={(value: number | undefined) => value !== undefined ? formatCurrency(value) : ''}
                    labelFormatter={(label) => `Mês: ${label}`}
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar 
                    dataKey="total" 
                    fill="#8b5cf6" 
                    radius={[4, 4, 0, 0]}
                    name="Receita"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center bg-surface-800/30 rounded-lg">
              <div className="text-center">
                <TrendingUp size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Sem dados de receita</p>
                <p className="text-xs text-surface-500 mt-1">no período selecionado</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default SupplierDashboard;
